import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type UseIndexedDbBaseOptions = { dbName?: string; storeName?: string }
export type UseIndexedDbOptions<T> =
  | ({ raw: true } & UseIndexedDbBaseOptions)
  | ({ raw: false; serializer: (value: T) => string; deserializer: (value: string) => T } & UseIndexedDbBaseOptions)

const DB_NAME = 'ztech-hooks'
const STORE_NAME = 'use-indexeddb'

function isBrowser() {
  return typeof window !== 'undefined' && typeof indexedDB !== 'undefined'
}

function openDb(dbName: string, storeName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, 1)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName)
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function idbGet<T = any>(key: string, dbName: string, storeName: string): Promise<T | undefined> {
  const db = await openDb(dbName, storeName)
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly')
    const store = tx.objectStore(storeName)
    const req = store.get(key)
    req.onsuccess = () => resolve(req.result as T | undefined)
    req.onerror = () => reject(req.error)
  })
}

async function idbSet<T = any>(key: string, value: T, dbName: string, storeName: string): Promise<void> {
  const db = await openDb(dbName, storeName)
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    const req = store.put(value as any, key)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

async function idbDel(key: string, dbName: string, storeName: string): Promise<void> {
  const db = await openDb(dbName, storeName)
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    const req = store.delete(key)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

export function useIndexedDb<T = any>(
  key: string,
  initialValue?: T,
  options?: UseIndexedDbOptions<T>
): [T | undefined, Dispatch<SetStateAction<T | undefined>>, () => void] {
  const resolvedOptions = useMemo<UseIndexedDbOptions<T>>(() => (options ? options : ({ raw: true } as any)), [options])
  const [state, setState] = useState<T | undefined>(initialValue)

  //setPersisted is memoized with [persist] and does not depend on key. If key changes later, a version of setPersisted
  // created earlier would still write to the old key unless it reads the latest from a ref. Using keyRef.current
  // guarantees the write goes to the current key.
  const keyRef = useRef(key)
  keyRef.current = key
  const aliveRef = useRef(true)
  const dbName = options?.dbName ?? DB_NAME
  const storeName = options?.storeName ?? STORE_NAME

  useEffect(() => {
    aliveRef.current = true
    if (!isBrowser()) {
      return () => {
        aliveRef.current = false
      }
    }
    let cancelled = false
    ;(async () => {
      try {
        const stored = await idbGet<any>(key, dbName, storeName)
        if (cancelled || !aliveRef.current) return

        if (stored === undefined) {
          if (typeof initialValue !== 'undefined') {
            const toStore = resolvedOptions.raw
              ? (initialValue as any)
              : (resolvedOptions as any).serializer(initialValue as T)
            await idbSet(key, toStore, dbName, storeName)
          }
          setState(initialValue)
        } else {
          const value = resolvedOptions.raw ? (stored as T) : (resolvedOptions as any).deserializer(stored as string)
          setState(value)
        }
      } catch {
        setState(initialValue)
      }
    })()
    return () => {
      cancelled = true
      aliveRef.current = false
    }
    // resolvedOptions intentionally excluded: it is a useMemo keyed on the `options` object, and the
    // one shipped consumer (useAutoDownloadFile) passes a new `options` literal every render, so
    // resolvedOptions would change every render too — re-running this fetch, calling setState, and
    // re-rendering the caller in a loop. Reading resolvedOptions here (deps unchanged) means an
    // `options.raw`/serializer swap after mount is picked up only on the next `key`/`dbName`/`storeName`/
    // `initialValue` change, not immediately — an accepted trade-off given no consumer swaps that mid-life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, dbName, storeName, initialValue])

  const persist = useCallback(
    async (k: string, v: T | undefined) => {
      if (!isBrowser()) return
      if (typeof v === 'undefined') {
        await idbDel(k, dbName, storeName)
        return
      }
      const toStore = resolvedOptions.raw ? (v as any) : (resolvedOptions as any).serializer(v)
      await idbSet(k, toStore, dbName, storeName)
    },
    [resolvedOptions, dbName, storeName]
  )

  const setPersisted: Dispatch<SetStateAction<T | undefined>> = useCallback(
    (update) => {
      setState((prev) => {
        const next = typeof update === 'function' ? (update as (p: T | undefined) => T | undefined)(prev) : update
        void persist(keyRef.current, next as T | undefined)
        return next
      })
    },
    [persist]
  )

  const clear = useCallback(() => {
    setState(undefined)
    void persist(keyRef.current, undefined)
  }, [persist])

  return [state, setPersisted, clear]
}
